import React, { useState, useRef, useCallback } from 'react';
import { AiFillCamera } from 'react-icons/ai';
import { MdCrop } from 'react-icons/md';
import { optimizeImage } from '../../hooks/ImageOptimizer';
import { errorAlert, convertBase64 } from '../../utils/utils';
import VisDiv from '../../whitaijiFrontendLibrary/components/VisDiv';
import { useStateContext } from '../../context/StateContext';
import ReactCrop, { centerCrop, makeAspectCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import T, { dT } from '../Translation';

const SquareCropPhoto = ({ saveFn }) => {
  const [loading, setLoading] = useState(false);
  const [src, setSrc] = useState(null);
  const [crop, setCrop] = useState();
  const [completedCrop, setCompletedCrop] = useState(null);
  const [wrongImageType, setWrongImageType] = useState(false);
  const imgRef = useRef(null);
  const hiddenFileInput = useRef(undefined);
  const canvasRef = useRef(null);
  const testing = false;
  const { hideTestButtons, language } = useStateContext();

  // Function to center the crop in a square aspect ratio
  const centerSquareCrop = useCallback((mediaWidth, mediaHeight) => {
    return centerCrop(
      makeAspectCrop(
        {
          unit: '%',
          width: 90,
        },
        1, // Square aspect ratio (1:1)
        mediaWidth,
        mediaHeight
      ),
      mediaWidth,
      mediaHeight
    );
  }, []);

  // Handle image load
  const onImageLoad = useCallback((e) => {
    const { width, height } = e.currentTarget;
    setCrop(centerSquareCrop(width, height));
  }, [centerSquareCrop]);

  const handleClick = (event) => {
    hiddenFileInput.current.click();
  };

  const onTestClick = () => {
    // Test functionality if needed
  };

  // Function to get a cropped image from canvas
  const getCroppedImg = useCallback(async () => {
    if (!completedCrop || !imgRef.current) return;

    const image = imgRef.current;
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;

    canvas.width = completedCrop.width;
    canvas.height = completedCrop.height;

    // Draw the cropped image onto the canvas
    ctx.drawImage(
      image,
      completedCrop.x * scaleX,
      completedCrop.y * scaleY,
      completedCrop.width * scaleX,
      completedCrop.height * scaleY,
      0,
      0,
      completedCrop.width,
      completedCrop.height
    );

    // Convert canvas to blob
    return new Promise((resolve) => {
      canvas.toBlob((blob) => {
        if (!blob) {
          console.error('Canvas is empty');
          return;
        }
        blob.name = 'cropped.jpeg';
        const croppedFile = new File([blob], 'cropped.jpeg', { type: 'image/jpeg' });
        resolve(croppedFile);
      }, 'image/jpeg');
    });
  }, [completedCrop]);

  // Handle crop completion
  const handleCropComplete = async () => {
    if (!completedCrop || !imgRef?.current) {
      errorAlert(dT({en: "Select an area to crop", it: "Seleziona un'area da ritagliare"}), language);
      return;
    }

    try {
      setLoading(true);
      const croppedFile = await getCroppedImg();
      const optimizedFile = await optimizeImage(croppedFile);
      const base64 = await convertBase64(optimizedFile);

      // Save the cropped image
      const doc = {
        base64,
        file: optimizedFile
      };
      console.log("knwkdlewjdljew", {fileSize: doc?.file?.size})
      if(doc?.file?.size > 500000) {

        setLoading(false);
        setWrongImageType(true);
        errorAlert(dT({en:"Error, Invalid image Size. File should be less than 500k", it:"Errore, file troppo grande. Deve essre minore di 500k "}, language))
        return;

      }
      saveFn(doc);
      
      // Reset state
      setSrc(null);
      setCrop(undefined);
      setCompletedCrop(null);
      setLoading(false);
    } catch (error) {
      console.error("Error processing cropped image:", error);
      errorAlert("Errore durante l'elaborazione dell'immagine");
      setLoading(false);
    }
  };

  const uploadImage = async (e) => {
    try {
      setLoading(true);
      const file = e.target.files[0];
       
      // Validate file type
      const validTypes = ['image/png', 'image/heic', 'image/svg', 'image/jpeg', 'image/gif', 'image/webp', 'image/tiff'];
      if (!validTypes.includes(file?.type)) {
        setLoading(false);
        setWrongImageType(true);
        errorAlert(dT({en:"Error, Invalid image type. Files supported: png, heic, svg, jpeg, gif, webp, tiff", it:"Errore, Tipologia Immagine errata. file accettati: png, heic, svg, jpeg, gif, webp, tiff "}, language))
        return;
      }
      // Convert to base64 for display
      const base64 = await convertBase64(file);
      setSrc(base64);
      setWrongImageType(false);
      setLoading(false);
    } catch (error) {
      console.error("Upload error:", error);
      setLoading(false);
      errorAlert("Errore durante il caricamento dell'immagine");
    }
  };

  return (
    <div className="square-crop-photo-container">
      <VisDiv rules={[testing]}>
        <button onClick={onTestClick}>test</button>
      </VisDiv>

      {!src ? (
        <button onClick={handleClick} className="round-button-no-hover">
          <AiFillCamera size={25} color={"white"} />
        </button>
      ) : (
        <div className="crop-container">
          <ReactCrop
            crop={crop}
            onChange={(c) => setCrop(c)}
            onComplete={(c) => setCompletedCrop(c)}
            aspect={1} // Force square aspect ratio
            circularCrop={false}
          >
            <img
              ref={imgRef}
              src={src}
              onLoad={onImageLoad}
              style={{ maxHeight: '70vh', maxWidth: '100%' }}
              alt="Upload"
            />
          </ReactCrop>
          
          <div className="crop-controls" style={{ marginTop: '10px', display: 'flex', justifyContent: 'left' }}>
            <button 
              onClick={handleCropComplete}
              className="crop-button"
              style={{ padding: '8px 16px', backgroundColor: 'green', color: 'white', border: 'none', borderRadius: '4px', display: 'flex', alignItems: 'center', gap: '5px' }}
              disabled={loading}
            >
              <MdCrop size={20} />
              {loading ? 
                dT({en: "Processing...", it: 'Elaborazione...'}, language) : dT({en: "Crop", it: "Ritaglia"}, language)}
            </button>
            <button 
              onClick={() => {
                setSrc(null);
                setCrop(undefined);
                setCompletedCrop(null);
              }}
              className="cancel-button"
              style={{ marginLeft: '12px', padding: '8px 16px', backgroundColor: '#f44336', color: 'white', border: 'none', borderRadius: '4px' }}
            >
                <T obj={{en: "Cancel", it: "Annulla"}}/>
              </button>
          </div>
        </div>
      )}

      <input
        type="file"
        name="upload-image"
        ref={hiddenFileInput}
        style={{ display: "none" }}
        onChange={(e) => uploadImage(e)}
        className="insert-photo-button"
      />
    </div>
  );
};

export default SquareCropPhoto;
